// src/utils/tableOrder.js
//
// Custom ordering of the Data-page table sidebar. A cosmetic, per-user
// preference, so it lives in localStorage like the sidebar width rather than
// in Firestore: nothing else in the app reads it, and it is scoped to the
// browser by design. The key carries both the company and the user id so two
// people sharing a machine — or one person emulating another organisation —
// never see each other's order.
//
// The stored value is the full list of table ids in display order. Tables not
// in the list (created after the last drag) keep their fetched order and land
// after the ordered ones; ids that no longer exist are ignored on read and
// dropped on the next write.

const STORAGE_PREFIX = 'decode.table_order';

export const tableOrderStorageKey = (companyId, uid) =>
  `${STORAGE_PREFIX}.${companyId || ''}.${uid || ''}`;

export function readStoredTableOrder(key) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key));
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    // Storage unavailable or malformed — fall back to the fetched order.
    return [];
  }
}

export function storeTableOrder(key, ids) {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Storage unavailable — the order just won't persist.
  }
}

// Apply a stored order to a table list: ordered tables first, by position;
// the rest afterwards in their existing order.
export function orderTables(tables, order) {
  if (!order || order.length === 0) return tables;
  const position = new Map(order.map((id, index) => [id, index]));
  const known = tables.filter((t) => position.has(t.id));
  const unknown = tables.filter((t) => !position.has(t.id));
  known.sort((a, b) => position.get(a.id) - position.get(b.id));
  return [...known, ...unknown];
}

// Move one entry of a rendered sub-list (e.g. the visible Decode tables) from
// `dragIndex` to `dropIndex`, and write that new sequence back into the full
// ordered id list without disturbing any id outside the sub-list. `subsetIds`
// must appear in `fullIds` in the same relative order, which holds because the
// sidebar derives every group from the ordered full list.
export function reorderWithinSubset(fullIds, subsetIds, dragIndex, dropIndex) {
  const reordered = [...subsetIds];
  const [moved] = reordered.splice(dragIndex, 1);
  reordered.splice(dropIndex, 0, moved);
  const subset = new Set(subsetIds);
  let next = 0;
  return fullIds.map((id) => (subset.has(id) ? reordered[next++] : id));
}
